import { clsx } from 'clsx'
import type { InputHTMLAttributes } from 'react'
import { useEffect, useState } from 'react'
import { getTrackBackground } from 'react-range'
import minusButton from '../../../assets/images/buttons/minus-button.svg'
import plusButton from '../../../assets/images/buttons/plus-red-button.svg'
import sliderButton from '../../../assets/images/buttons/slider-button.svg'
import { getStepForInputByMinMax } from '../../../utils'
import { Icon, Range } from '../../atoms'
import { InputWrapper } from '../InputWrapper'
import {
  buttonStyle,
  inputContainer,
  inputStyle,
  inputWrappedStyle,
  rangeInputContainer,
  rangeInputStyle,
  textAlignCenter,
  textAlignRight,
  thumbStyle,
} from './index.css'

export const InputRanged = (
  props: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & {
    value: number
    min: number
    max: number
    showBigBlindLabel?: boolean
    classNameContainer?: string
    onChange: (value: number) => void
  },
) => {
  const { showBigBlindLabel, classNameContainer, ...restProps } = props
  const [value, setValue] = useState(
    props.max
      ? Math.min(props.value ?? props.min, props.max)
      : Math.max(props.value ?? props.min),
  )
  const [step, setStep] = useState(
    props.step
      ? Number(props.step)
      : getStepForInputByMinMax(props.min, props.max),
  )
  const areTresholdsEqual = props.min >= props.max

  const onChange = (value: number | undefined) => {
    if (typeof value === 'undefined') {
      return
    }
    setValue(value)
    props.onChange(value)
  }

  const onClickButton = (side: '+' | '-') => {
    const newValue = side === '+' ? Number(value) + step : Number(value) - step
    if (newValue > props.max || newValue < props.min) {
      return
    }
    const formatedNewValue = Number(
      (Math.floor(newValue * 100) / 100).toFixed(2),
    )
    setValue(formatedNewValue)
    props.onChange(formatedNewValue)
  }

  useEffect(() => {
    setValue(props.value)
  }, [props.value])

  useEffect(() => {
    setStep(() =>
      props.step
        ? Number(props.step)
        : getStepForInputByMinMax(props.min, props.max),
    )
  }, [props.step, props.min, props.max])

  return (
    <div className={clsx(inputContainer, props.classNameContainer)}>
      <InputWrapper
        {...restProps}
        className={clsx(props.className, inputStyle)}
        classNameWrapped={clsx(
          inputWrappedStyle,
          props.showBigBlindLabel ? textAlignRight : textAlignCenter,
        )}
        hideNumberArrows={true}
        value={value}
        type='number'
        // size={value.toString().length + 1}
        onChange={onChange}
      >
        {props.showBigBlindLabel ? (
          <span
            style={{ width: '100%', alignSelf: 'center', justifySelf: 'start' }}
          >
            BB
          </span>
        ) : null}
      </InputWrapper>
      <div className={rangeInputContainer}>
        <Icon
          src={minusButton}
          className={buttonStyle}
          onClick={() => onClickButton('-')}
        />
        <Range
          values={[value]}
          min={areTresholdsEqual ? value - 1 : props.min}
          max={areTresholdsEqual ? value : props.max}
          disabled={areTresholdsEqual}
          step={step}
          onChange={([value]) => onChange(value)}
          renderTrack={({ props: trackProps, children }) => (
            <div
              {...trackProps}
              className={rangeInputStyle}
            >
              <div
                ref={trackProps.ref}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '2px',
                  width: '100%',
                  background: getTrackBackground({
                    values: [value],
                    colors: ['#828282', '#4D4D4D'],
                    min: props.min,
                    max: props.max,
                  }),
                  alignSelf: 'center',
                }}
              >
                {children}
              </div>
            </div>
          )}
          renderThumb={({ props: propsThumb, value }) => {
            return (
              <div
                {...propsThumb}
                key={propsThumb.key}
                style={{
                  ...propsThumb.style,
                  left: `${((value - props.min) / (props.max - props.min)) * 84}%`,
                }}
                className={thumbStyle}
              >
                <Icon src={sliderButton} />
              </div>
            )
          }}
        />
        <Icon
          src={plusButton}
          className={buttonStyle}
          onClick={() => onClickButton('+')}
        />
      </div>
    </div>
  )
}
